import React, { useEffect, useRef } from "react";
import videojs from "video.js";
import useFrontPageCta from "../../../Hooks/useFrontPageCta";

// Components
import ButtonPill from "../../Buttons/ButtonPill";
import ButtonPillGradient from "../../Buttons/ButtonPillGradient";

// Assets
import IconPlayButton from "../../../Assets/SVGs/Icons/VideoPlayer/PlayButton";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

// Helpers
import { formatMoney } from "../../../Helpers/Currency/Currency";
import { withPromotionCode } from "../../../Helpers/PromotionCode";
import {
    getMonthlyRenewalCopy,
    resolveMarketingPlanPricing,
    selectMonthlyMarketingPlan,
} from "../../../Helpers/PlanPricing";

// Models
import PlanDTO from "../../../Models/DTOs/Plan";

const HERO_VIDEO_URL =
    "https://storage.ickonic.com/IckonicVideo/asset-15ccda1d-bf35-451f-9833-4e2e0fe730ff/hero-video-v2.m3u8";

interface FrontHeroProps {
    onOpenTrailer: () => void;
    plans: PlanDTO[] | null;
}

const FrontHero: React.FC<FrontHeroProps> = ({ onOpenTrailer, plans }) => {
    const backgroundRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const background = backgroundRef.current;
        if (!background || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        // Video.js owns this element so disposal also works during Strict Mode remounts.
        const video = document.createElement("video");
        video.className = "video-js";
        video.muted = true;
        video.playsInline = true;
        video.tabIndex = -1;
        background.appendChild(video);

        const player = videojs(video, {
            autoplay: true,
            controls: false,
            muted: true,
            loop: true,
            playsinline: true,
            preload: "auto",
            sources: [{ src: HERO_VIDEO_URL, type: "application/x-mpegURL" }],
        });

        return () => {
            if (!player.isDisposed()) player.dispose();
        };
    }, []);

    const monthlyPlan = selectMonthlyMarketingPlan(plans);
    const pricing = resolveMarketingPlanPricing(monthlyPlan);
    const initialPrice = pricing
        ? formatMoney(pricing.initialPrice, pricing.currency)
        : null;
    const renewalPrice = pricing
        ? formatMoney(pricing.renewalPrice, pricing.currency)
        : null;
    const primaryActionText = initialPrice
        ? `Start watching for ${initialPrice}`
        : "Start watching";
    const cta = useFrontPageCta(primaryActionText, withPromotionCode(RoutePaths.SignUp[0]));
    const renewalText = getMonthlyRenewalCopy(
        pricing,
        initialPrice,
        renewalPrice
    );

    return (
        <section id="top" className="front__hero" aria-labelledby="front-hero-heading">
            <div ref={backgroundRef} className="front-hero__background" aria-hidden="true" />

            <div className="front-hero__inner">
                <div className="front-hero__content">
                    <h1 id="front-hero-heading">
                        Groundbreaking films and series,{" "}
                        <em>without the mainstream spin.</em>
                    </h1>

                    <p className="front-hero__copy">
                        Stream original documentaries, independent current affairs,
                        thought-provoking series and over 30 years of David Icke
                        content&mdash;all in one place.
                    </p>

                    <div className="front-hero__actions">
                        <ButtonPillGradient
                            text={cta.text}
                            link={cta.link}
                            className="front-hero__primary-action"
                            background="linear-gradient(225deg, rgba(187, 54, 184, 1) 0%, #b760b4 100%)"
                            backgroundHover="#b760b4"
                            color="white"
                        />

                        <ButtonPill
                            label="Watch the trailer"
                            className="front-hero__trailer-action"
                            onClick={onOpenTrailer}
                            icon={
                                <span className="front-hero__play-icon" aria-hidden="true">
                                    {IconPlayButton({ iconFill: "white" })}
                                </span>
                            }
                        />
                    </div>

                    {renewalText && (
                        <p className="front-hero__renewal">
                            {renewalText}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
};

export default FrontHero;
